import {css, StyleSheet} from "aphrodite";

import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {AppleButton} from "@khan/auth-apple";
import {CleverLibraryButton} from "@khan/auth-clever";
import {FacebookButton} from "@khan/auth-facebook";
import {GoogleButton} from "@khan/auth-google";
import {MicrosoftButton} from "@khan/auth-microsoft";
import {authType, type Role} from "@khan/auth-shared";
import {RequestInfo} from "@khan/shared-core";

interface Props {
    under13: boolean;
    role: Role;
    continueUrl?: string;
    classCode?: string;
    iconOnly?: boolean;
    size: "large" | "medium" | "small";
    setServerError: (message: string) => void;
    showFacebookButton?: boolean;
    birthdate?: string;
    validationRequired?: boolean;
    isFormValid?: boolean;
    onValidationError?: () => void;
}

export const SignupOauthButtons = ({
    under13,
    role,
    continueUrl,
    classCode,
    iconOnly,
    size,
    setServerError,
    showFacebookButton,
    birthdate,
    validationRequired,
    isFormValid,
    onValidationError,
}: Props) => {
    const showCleverSignUp =
        RequestInfo.kaLocale === "en" && role === "teacher";
    const shouldShowMicrosoft = useFlagOrCookie("microsoft-sso");

    // under-13s are allowed to sign up using google/facebook,
    // IF they have class codes.
    const treatAsUnder13 = under13 && !classCode;

    const commonButtonProps = {
        size,
        role,
        continueUrl,
        classCodes: classCode ? [classCode] : undefined,
        onError: setServerError,
        iconOnly,
        validationRequired,
        isFormValid,
        onValidationError,
    };

    return (
        <>
            {!treatAsUnder13 && (
                <ListItem iconOnly={false} style={styles.googleButtonWrapper}>
                    <GoogleButton
                        {...commonButtonProps}
                        iconOnly={false}
                        minorConversions={[
                            {id: "signup_google_button_clicked"},
                        ]}
                        cedarEventType="SignupGoogleButtonClicked"
                        birthdate={birthdate}
                        context={authType.SIGNUP}
                    />
                </ListItem>
            )}
            {showCleverSignUp && (
                <ListItem iconOnly={iconOnly}>
                    <CleverLibraryButton
                        size="large"
                        isLogin={false}
                        iconOnly={iconOnly}
                        validationRequired={validationRequired}
                        isFormValid={isFormValid}
                        onValidationError={onValidationError}
                    />
                </ListItem>
            )}
            {!under13 && showFacebookButton && (
                <ListItem iconOnly={iconOnly}>
                    <FacebookButton
                        {...commonButtonProps}
                        minorConversions={[
                            {id: "signup_facebook_button_clicked"},
                        ]}
                        cedarEventType="SignupFacebookButtonClicked"
                        onError={(message) => {
                            setServerError(message);
                        }}
                        context={authType.SIGNUP}
                    />
                </ListItem>
            )}
            {!under13 && (
                <ListItem iconOnly={iconOnly}>
                    <AppleButton
                        {...commonButtonProps}
                        minorConversions={[{id: "signup_apple_button_clicked"}]}
                        cedarEventType="SignupAppleButtonClicked"
                        onError={(errorMessage) => {
                            setServerError(errorMessage);
                        }}
                        context={authType.SIGNUP}
                    />
                </ListItem>
            )}
            {!under13 && shouldShowMicrosoft && (
                <ListItem iconOnly={iconOnly}>
                    <MicrosoftButton
                        {...commonButtonProps}
                        cedarEventType="SignupMicrosoftButtonClicked"
                        onError={(errorMessage) => {
                            setServerError(errorMessage);
                        }}
                        isSignup={true} // As opposed to login
                    />
                </ListItem>
            )}
        </>
    );
};

/**
 * Styled component for the oauth button list.
 */
function ListItem({
    iconOnly,
    children,
    style,
}: {
    iconOnly?: boolean;
    children: React.ReactNode;
    style?: React.CSSProperties;
}): React.ReactElement {
    return (
        <li
            className={css(
                styles.signupButtonWrapper,
                styles.listItem,
                iconOnly ? styles.listItemIconOnly : null,
                style ? style : null,
            )}
        >
            {children}
        </li>
    );
}

const styles = StyleSheet.create({
    signupButtonWrapper: {
        marginBlockStart: 4,
    },
    listItem: {
        marginBlockEnd: spacing.xxxSmall_4,
    },
    listItemIconOnly: {
        margin: 0,
        flex: "1 0 auto",
    },
    googleButtonWrapper: {
        width: "100%",
    },
});
